import { useMemo } from 'react';
import { useFilter } from 'react-aria-components';

export interface UseLocalFilterOptions<T extends object> {
  options: T[];
  inputValue: string;
  getOptionLabel: (option: T) => string;
  filterOptions: boolean;
  isSearching: boolean;
}

export function useLocalFilter<T extends object>({
  options,
  inputValue,
  getOptionLabel,
  filterOptions,
  isSearching,
}: UseLocalFilterOptions<T>): T[] {
  // Locale-aware, case-insensitive substring match — react-aria-components' own
  // ComboBox defaultFilter. `sensitivity: 'accent'` ignores case but respects
  // diacritics, matching the previous toLowerCase().includes() (case-insensitive,
  // accent-sensitive) for the field's product labels.
  const { contains } = useFilter({ sensitivity: 'accent' });

  // Filter options based on input (only if filterOptions is true and user is actively searching)
  const filteredOptions = useMemo(() => {
    const safeOptions = options ?? [];
    if (!filterOptions) return safeOptions;
    if (!isSearching) return safeOptions;
    if (!inputValue) return safeOptions;
    return safeOptions.filter((option) => contains(getOptionLabel(option), inputValue));
  }, [options, inputValue, getOptionLabel, filterOptions, isSearching, contains]);

  return filteredOptions;
}
